import {
	Box,
	Button,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import React, { useState } from "react";
import type { IInvoicePayment } from "../type/exhibitorType";
import CommonPdfAndImageView from "src/common/CommonPdfAndImageView";
import dayjs from "dayjs";
import { getStatusLabelInvoiceStatus } from "src/Utils/Combined";
import PaymentDetailsModal from "./PaymentDetailsModal";
import UpdatePaymentStatusModal from "./UpdatePaymentStatusModal";

const InvoicePaymentComm = ({
	invoicePayment,
	type = "b2b",
}: {
	invoicePayment: IInvoicePayment[];
	type?: string;
}) => {
	const [openModal, setOpenModal] = useState<boolean>(false);
	const [selectedPayment, setSelectedPayment] =
		useState<IInvoicePayment | null>(null);

	const [openStatusModal, setOpenStatusModal] = useState<boolean>(false);
	const [paymentForStatusUpdate, setPaymentForStatusUpdate] =
		useState<IInvoicePayment | null>(null);

	const handleViewDetails = (payment: IInvoicePayment) => {
		setSelectedPayment(payment);
		setOpenModal(true);
	};

	const handleOpenStatusUpdate = (payment: IInvoicePayment) => {
		setPaymentForStatusUpdate(payment);
		setOpenStatusModal(true);
	};

	return (
		<Box translate="no" className="notranslate">
			<Typography
				textAlign='center'
				variant='h4'
				gutterBottom
				sx={{
					marginBottom: "20px",
					textDecoration: "underline",
				}}
			>
				<span>Payment Details</span>
			</Typography>

			<Table>
				<TableHead>
					<TableRow>
						<TableCell><span>SL.</span></TableCell>
						<TableCell><span>Date</span></TableCell>
						<TableCell><span>Payment Type</span></TableCell>
						<TableCell><span>Bank Account</span></TableCell>
						<TableCell><span>Bank Name</span></TableCell>
						<TableCell><span>Bank Slip</span></TableCell>
						<TableCell><span>Status</span></TableCell>
						<TableCell><span>Amount</span></TableCell>
						<TableCell><span>Action</span></TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{invoicePayment?.map((row, index) => {
						const hasPaymentDetails = Boolean(
							row?.payment_details ||
							row?.payment_type?.toLowerCase() === "online"
						);

						return (
							<TableRow key={row.id}>
								<TableCell><span>{index + 1}</span></TableCell>
								<TableCell>
									<Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
										{row.payment_date && (
											<Typography
												variant="body2"
												sx={{ fontSize: "0.825rem", fontWeight: 500 }}
											>
												<span>Payment: </span>
												<span>{dayjs(row.payment_date).format("DD-MMM-YYYY")}</span>
											</Typography>
										)}
										{row.deposit_date && (
											<Typography
												variant="caption"
												color="text.secondary"
												sx={{ fontSize: "0.75rem" }}
											>
												<span>Deposit: </span>
												<span>{dayjs(row.deposit_date).format("DD-MMM-YYYY")}</span>
											</Typography>
										)}
										{!row.payment_date && !row.deposit_date && (
											<Typography variant="body2" color="text.secondary">
												<span>N/A</span>
											</Typography>
										)}
									</Box>
								</TableCell>
								<TableCell><span>{row.payment_type || "N/A"}</span></TableCell>
								<TableCell><span>{row.bank_ac_no || "N/A"}</span></TableCell>
								<TableCell><span>{row.bank_name || "N/A"}</span></TableCell>

								<TableCell>
									<CommonPdfAndImageView
										fileName='Bank Slip'
										file={row?.bank_slip}
									/>
								</TableCell>

								<TableCell>
									<Box
										sx={{
											display: "flex",
											flexDirection: "column",
											alignItems: "flex-start",
											gap: 0.5,
										}}
									>
										{getStatusLabelInvoiceStatus(row?.status)}

										{hasPaymentDetails && (
											<Button
												size='small'
												variant='outlined'
												color='primary'
												onClick={() => handleViewDetails(row)}
												sx={{
													fontSize: "0.72rem",
													py: 0.2,
													px: 1,
													minWidth: "unset",
													textTransform: "none",
													borderRadius: 1,
													lineHeight: 1.2,
													mt: 0.3,
												}}
											>
												<span>View Details</span>
											</Button>
										)}
									</Box>
								</TableCell>
								<TableCell><span>{row.amount}</span></TableCell>
								<TableCell>
									<Button
										size='small'
										variant='outlined'
										color='primary'
										onClick={() => handleOpenStatusUpdate(row)}
										sx={{
											fontSize: "0.72rem",
											py: 0.25,
											px: 1,
											minWidth: "unset",
											textTransform: "none",
											borderRadius: 1,
											lineHeight: 1.2,
										}}
									>
										<span>Update Status</span>
									</Button>
								</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>

			{/* Payment Details Modal */}
			<PaymentDetailsModal
				open={openModal}
				handleClose={() => {
					setOpenModal(false);
					setSelectedPayment(null);
				}}
				payment={selectedPayment}
			/>

			{/* Update Payment Status Modal */}
			<UpdatePaymentStatusModal
				open={openStatusModal}
				handleClose={() => {
					setOpenStatusModal(false);
					setPaymentForStatusUpdate(null);
				}}
				payment={paymentForStatusUpdate}
				apiEndpoint={
					paymentForStatusUpdate
						? `/api/admin/${type || "b2b"}/invoice/payment/${paymentForStatusUpdate.id}`
						: undefined
				}
			/>
		</Box>
	);
};

export default InvoicePaymentComm;
